import React from "react";
import { formatMoney } from "../lib/format";
import { useSettings } from "../SettingsContext";

const MS_DAY = 24 * 60 * 60 * 1000;

const GROUPS = [
  { key: "overdue", label: "Overdue", labelClass: "text-red-600", badgeHeader: "bg-red-500" },
  { key: "due_soon", label: "Due Soon", labelClass: "text-amber-600", badgeHeader: "bg-amber-500" },
  { key: "upcoming", label: "Upcoming", labelClass: "text-slate-600", badgeHeader: "bg-slate-600" },
  { key: "paid", label: "Paid", labelClass: "text-green-600", badgeHeader: "bg-green-500" },
  { key: "none", label: "No Due Date", labelClass: "text-gray-500", badgeHeader: "bg-gray-400" },
];

function dueDateOf(account) {
  if (!account.payment_due_date) return null;
  const d = new Date(account.payment_due_date + "T00:00:00");
  return isNaN(d.getTime()) ? null : d;
}

// For Contract accounts, the "next due" shown on the card is the earliest
// non-completed requirement date (falls back to any payment_due_date).
function nextRequirementDate(account) {
  const reqs = (account.requirements || [])
    .filter((r) => !r.completed && r.due_date)
    .map((r) => new Date(r.due_date + "T00:00:00"))
    .filter((d) => !isNaN(d.getTime()))
    .sort((a, b) => a - b);
  return reqs[0] || null;
}

function effectiveDueDate(account) {
  if (account.account_type === "Contract") {
    return nextRequirementDate(account) || dueDateOf(account);
  }
  return dueDateOf(account);
}

function groupKeyFor(account, today) {
  if (account.is_paid) return "paid";
  const due = effectiveDueDate(account);
  if (!due) return "none";
  if (due < today) return "overdue";
  if (due.getTime() - today.getTime() <= 7 * MS_DAY) return "due_soon";
  return "upcoming";
}

const formatAmount = (amount, settings) => formatMoney(amount, settings);

const DueBadge = ({ account, badgeHeader }) => {
  const due = effectiveDueDate(account);
  if (!due) {
    return (
      <div className="w-11 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 shadow-sm text-center" data-testid={`due-badge-empty-${account.id}`}>
        <div className={`${badgeHeader} text-white text-[10px] font-bold uppercase tracking-wide py-0.5`}>—</div>
        <div className="bg-white text-gray-300 text-base font-bold leading-tight py-1">–</div>
      </div>
    );
  }
  return (
    <div className="w-11 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 shadow-sm text-center" data-testid={`due-badge-${account.id}`}>
      <div className={`${badgeHeader} text-white text-[10px] font-bold uppercase tracking-wide py-0.5`}>
        {due.toLocaleDateString(undefined, { month: "short" })}
      </div>
      <div className="bg-white text-gray-900 text-base font-bold leading-tight py-1">{due.getDate()}</div>
    </div>
  );
};

const AccountCard = ({ account, group, isSelected, onSelect, logoUrl }) => {
  const { settings } = useSettings();
  return (
  <div
    onClick={() => onSelect(account)}
    data-testid={`account-card-${account.id}`}
    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
      isSelected ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-white hover:border-gray-300"
    }`}
  >
    <div className="flex items-start gap-2.5">
      <DueBadge account={account} badgeHeader={group.badgeHeader} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-gray-900 truncate text-sm">{account.name}</h3>
          {logoUrl && (
            <img
              src={logoUrl}
              alt={`${account.name} logo`}
              className="h-6 max-w-[56px] rounded object-contain border bg-white flex-shrink-0"
            />
          )}
        </div>
        <p className="text-xs text-gray-600 mt-0.5">{account.account_type}</p>
        <div className="flex items-center justify-between mt-1.5 gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                account.status === "Active"
                  ? "bg-green-100 text-green-800"
                  : account.status === "Inactive"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {account.status}
            </span>
            {account.amount_due != null && (
              <span
                className={`text-xs font-semibold ${account.is_paid ? "text-green-600" : "text-gray-900"}`}
                data-testid={`amount-due-${account.id}`}
              >
                {formatAmount(account.amount_due, settings)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

const AccountsList = ({ accounts, selectedAccount, onSelect, logoUrls }) => {
  const { settings } = useSettings();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const grouped = {};
  accounts.forEach((a) => {
    const key = groupKeyFor(a, today);
    (grouped[key] = grouped[key] || []).push(a);
  });
  Object.values(grouped).forEach((list) =>
    list.sort((a, b) => {
      const da = effectiveDueDate(a);
      const db = effectiveDueDate(b);
      if (da && db) return da - db;
      if (da) return -1;
      if (db) return 1;
      return a.name.localeCompare(b.name);
    })
  );

  return (
    <div className="space-y-4" data-testid="accounts-list">
      {GROUPS.filter((g) => grouped[g.key]?.length).map((group) => (
        <div key={group.key} data-testid={`account-group-${group.key}`}>
          <div className="flex items-center gap-2 mb-2 px-1">
            <span className={`text-xs font-bold uppercase tracking-wide ${group.labelClass}`}>{group.label}</span>
            <span className="text-xs text-gray-400">({grouped[group.key].length})</span>
            {(() => {
              const total = grouped[group.key].reduce((s, a) => s + (a.amount_due || 0), 0);
              return total > 0 ? (
                <span className={`text-xs font-semibold ml-auto ${group.labelClass}`} data-testid={`group-total-${group.key}`}>
                  {formatAmount(total, settings)}
                </span>
              ) : null;
            })()}
          </div>
          <div className="space-y-2">
            {grouped[group.key].map((account) => (
              <AccountCard
                key={account.id}
                account={account}
                group={group}
                isSelected={selectedAccount?.id === account.id}
                onSelect={onSelect}
                logoUrl={account.logo_filename ? logoUrls[account.id] : null}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AccountsList;
